import { Fragment, useEffect, useMemo, useState } from "react";
import { FieldValues } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { MiBuildHouseAlt } from "@miplatform/assets";
import {
  MiAlert,
  MiButtonGroup,
  MiPlaceholderArea,
} from "@miplatform/components";
import { ICONS_WEB } from "@miplatform/constants";
import { IOrderTemplateResponse, IVerticalTabProps } from "@miplatform/types";
import * as mdi from "@mdi/js";
import { mdiChartSankeyVariant, mdiFormatListBulleted } from "@mdi/js";
import { Footer, VerticalTabsLayout } from "@mibuildweb/components";
import { Box, Typography } from "@mui/material";
import { ChartView } from "./components/ChartView/ChartView";
import { DependencyCard } from "./components/DependencyCard";
import { Separator, getSeparatorConfig } from "./components/Separator";
import { ConfigPageLayout } from "../../ConfigPageLayout";
import {
  getItemDependencyLabels,
  getItemSelfLabels,
  getValidationErrorsByItemId,
  validateDependencyMap,
} from "./autoTrigger.utils";
import { useAutoTriggerModel } from "./useAutoTriggerModel";

const DISPLAY_MODE_OPTIONS = [
  {
    label: "List",
    icon: mdiFormatListBulleted,
    value: "list",
    sx: {
      minWidth: "70px",
    },
  },
  {
    label: "Chart",
    icon: mdiChartSankeyVariant,
    value: "chart",
    sx: {
      minWidth: "70px",
    },
  },
] as const;

type DisplayModeOption = (typeof DISPLAY_MODE_OPTIONS)[number];

const DisplayModeSwitcher = ({
  activeView,
  onViewChange,
}: {
  activeView: "list" | "chart";
  onViewChange: (view: "list" | "chart") => void;
}) => {
  return (
    <MiButtonGroup
      selected={activeView}
      data={[...DISPLAY_MODE_OPTIONS]}
      handleClick={(item: DisplayModeOption) => onViewChange(item.value)}
    />
  );
};

export const AutoTrigger = () => {
  const [selectedDependency, setSelectedDependency] = useState<{
    day: number;
    id: string;
  } | null>(null);
  const [offsetFormValidityById, setOffsetFormValidityById] = useState<
    Record<string, boolean>
  >({});
  const [activeView, setActiveView] = useState<"list" | "chart">("list");
  const [selectedTab, setSelectedTab] =
    useState<IVerticalTabProps<FieldValues> | null>(null);

  const { classes, theme } = useStyles({ activeView });

  const {
    orderTemplates,
    fetchOrderTemplates,
    getOrderTemplateWorkOrder,
    sortedDependencyMap,
    labels,
    isDirty,
    addDependency,
    moveItemToDayGroup,
    updateDependencyField,
    startLinkEdit,
    cancelLinkEdit,
    confirmLinkEdit,
    save,
    reset,
    isLoading,
  } = useAutoTriggerModel();

  useEffect(() => {
    fetchOrderTemplates();
  }, []);

  useEffect(() => {
    if (selectedTab?.value)
      getOrderTemplateWorkOrder(String(selectedTab.value));
  }, [selectedTab]);

  const display: IOrderTemplateResponse | null = useMemo(() => {
    const index = orderTemplates.findIndex(
      (x) => x.id === String(selectedTab?.value)
    );
    if (index !== -1) {
      return orderTemplates[index];
    }
    return null;
  }, [selectedTab, orderTemplates]);

  const days = useMemo(() => {
    return Object.keys(sortedDependencyMap)
      .map((x) => Number(x))
      .sort((a, b) => a - b);
  }, [sortedDependencyMap]);

  const validation = useMemo(
    () => validateDependencyMap(sortedDependencyMap),
    [sortedDependencyMap]
  );

  const validationErrors = useMemo(
    () => getValidationErrorsByItemId(validation),
    [validation]
  );

  const currentDependencyItemIds = useMemo(
    () => new Set(Object.values(sortedDependencyMap).flat().map((item) => item.id)),
    [sortedDependencyMap]
  );

  const hasInvalidOffsetForms = useMemo(
    () =>
      Object.entries(offsetFormValidityById).some(
        ([itemId, isValid]) =>
          currentDependencyItemIds.has(itemId) && isValid === false
      ),
    [currentDependencyItemIds, offsetFormValidityById]
  );

  const isEmpty = useMemo(() => {
    return (
      days.length === 0 ||
      !Object.values(sortedDependencyMap).some((items) => items.length > 0)
    );
  }, [days, sortedDependencyMap]);

  const dependencyToLink = useMemo(() => {
    if (!selectedDependency) return null;
    return (
      sortedDependencyMap[selectedDependency.day]?.find(
        (x) => x.id === selectedDependency.id
      ) ?? null
    );
  }, [selectedDependency, sortedDependencyMap]);

  const renderAccordionContent = () => {
    if (isEmpty) {
      return (
        <MiPlaceholderArea
          text="No Records To Display"
          bgColor={theme.palette.primary.light}
          iconComponent={<img src={MiBuildHouseAlt} alt="House Icon" />}
        />
      );
    }

    if (activeView === "chart") {
      return (
        <ChartView
          sortedDependencyMap={sortedDependencyMap}
          labels={labels}
          orderTemplateName={display?.name ?? null}
        />
      );
    }

    return (
      <Box className={classes.container}>
        {days.map((x) => {
          const config = getSeparatorConfig(x);
          return (
            <Fragment key={`day-group-${x}`}>
              <Separator title={config.title} type={config.type} />
              {sortedDependencyMap[x].map((y) => {
                const isChecked =
                  dependencyToLink?.dependentOn.findIndex(
                    (dep) => dep === y.id
                  ) !== -1;
                return (
                  <Fragment key={`dependency-maps-${y.id}`}>
                    <DependencyCard
                      itemId={y.id}
                      indentation={Number(y.dependencyLabel)}
                      title={y.dependent.name}
                      isAddingDependency={dependencyToLink?.id === y.id}
                      canBeSelected={
                        dependencyToLink !== null &&
                        dependencyToLink?.id !== y.id
                      }
                      isDisable={
                        dependencyToLink !== null &&
                        y.days > dependencyToLink?.days &&
                        dependencyToLink?.id !== y.id
                      }
                      hours={String(y.hours)}
                      days={String(y.days)}
                      editDays={(value) => moveItemToDayGroup(x, y.id, value)}
                      editHours={(value) =>
                        updateDependencyField(
                          x,
                          y.id,
                          "hours",
                          Math.min(value, 23)
                        )
                      }
                      offsetTargetProperty={y.offsetTargetProperty}
                      editOffsetTargetProperty={(value) =>
                        updateDependencyField(
                          x,
                          y.id,
                          "offsetTargetProperty",
                          value
                        )
                      }
                      onOffsetFormValidityChange={(itemId, isValid) =>
                        setOffsetFormValidityById((prev) =>
                          prev[itemId] === isValid
                            ? prev
                            : { ...prev, [itemId]: isValid }
                        )
                      }
                      onCancel={() => {
                        cancelLinkEdit();
                        setSelectedDependency(null);
                      }}
                      onConfirm={() => {
                        confirmLinkEdit();
                        setSelectedDependency(null);
                      }}
                      onCheck={() => {
                        if (dependencyToLink) {
                          addDependency(dependencyToLink, y, !isChecked);
                        }
                      }}
                      isChecked={isChecked}
                      setAddingDependency={() => {
                        startLinkEdit();
                        setSelectedDependency({
                          day: y.days,
                          id: y.id,
                        });
                      }}
                      labels={getItemSelfLabels(labels, y.id)}
                      dependencyLabels={getItemDependencyLabels(labels, y.id)}
                    />
                    {dependencyToLink?.id === y.id && (
                      <MiAlert
                        isOpen
                        variant="info"
                        description={
                          <>
                            Select the orders which
                            <b>{` ${y.dependent.name} `}</b>
                            is dependent on.
                          </>
                        }
                        leftIcon={
                          ICONS_WEB.INFORMATION_OUTLINE as keyof typeof mdi
                        }
                        iconColor={theme.palette.primary.main}
                        sx={{
                          py: theme.spacing(0),
                          px: theme.spacing(3),
                          fontSize: theme.fontSize.small,
                          fontWeight: theme.fontWeight.medium,
                          "& .MuiAlert-action": {
                            py: theme.spacing(0),
                            px: theme.spacing(1),
                          },
                        }}
                        hideCloseButton
                      />
                    )}
                    {validationErrors[y.id] && (
                      <MiAlert
                        isOpen
                        variant="error"
                        description={validationErrors[y.id].find(() => true)}
                        sx={{
                          py: theme.spacing(0),
                          px: theme.spacing(3),
                          fontSize: theme.fontSize.small,
                          "& .MuiAlert-action": {
                            py: theme.spacing(0),
                            px: theme.spacing(1),
                          },
                        }}
                      />
                    )}
                  </Fragment>
                );
              })}
              {display && x === 0 && (
                <div className={classes.orderCard}>{display.name} Order</div>
              )}
            </Fragment>
          );
        })}
      </Box>
    );
  };

  return (
    <ConfigPageLayout
      hideOnBack
      loading={isLoading}
      title="Auto-Trigger"
      titleActionComponent={
        <DisplayModeSwitcher
          activeView={activeView}
          onViewChange={setActiveView}
        />
      }
    >
      <VerticalTabsLayout
        hideStatusIcons
        hideNavigationButtons
        tabs={orderTemplates.map((x) => ({
          label: x.name,
          value: x.id,
          filter: { isActive: true },
        }))}
        onTabChange={(x) => {
          setSelectedTab(x);
          setActiveView("list");
        }}
        transparentBackground
      >
        <Box className={classes.root}>
          {selectedTab?.value && (
            <Box className={classes.headerWrapper}>
              <Box className={classes.header}>
                <Typography
                  variant="HD4"
                  color={
                    activeView === "list"
                      ? theme.palette.common.white
                      : theme.palette.primary.medium
                  }
                >
                  {`${selectedTab?.label} Order${
                    activeView === "chart" ? " Chart" : ""
                  }`}
                </Typography>
              </Box>
              <Box className={classes.contentWrapper}>
                {renderAccordionContent()}
              </Box>
            </Box>
          )}
        </Box>
        <Footer
          show={isDirty}
          saveTitle="Save Flow"
          disableSave={
            Object.keys(validationErrors).length > 0 ||
            hasInvalidOffsetForms ||
            dependencyToLink !== null
          }
          isDirty={isDirty}
          handleSaveClick={() => {
            if (selectedTab?.value) save(String(selectedTab.value));
          }}
          handleCancelClick={reset}
        />
      </VerticalTabsLayout>
    </ConfigPageLayout>
  );
};

const useStyles = makeStyles<{ activeView: "list" | "chart" }>()(
  (theme, { activeView }) => ({
    container: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
    },
    root: {
      display: "flex",
      flexDirection: "column",
      flex: 1,
      minHeight: 0,
      gap: theme.spacing(2),
    },
    headerWrapper: {
      display: "flex",
      flexDirection: "column",
      flex: 1,
      minHeight: 0,
    },
    header: {
      backgroundColor:
        activeView === "chart"
          ? theme.palette.primary.light
          : theme.palette.primary.medium,
      padding: theme.spacing(0, 3),
      borderRadius: 4,
      minHeight: 42,
      display: "flex",
      alignItems: "center",
    },
    contentWrapper: {
      backgroundColor: theme.palette.background.paper,
      padding: theme.spacing(2, 3, 3),
      borderRadius: "0 0 4px 4px",
      flex: 1,
      display: "flex",
      flexDirection: "column",
      overflow: "auto",
    },
    orderCard: {
      display: "flex",
      backgroundColor: theme.palette.purple.light,
      border: `1px solid #DAC2FF`,
      borderRadius: theme.spacing(1),
      color: theme.palette.deepPurple.medium,
      fontWeight: theme.fontWeight.semiBold,
      fontSize: theme.fontSize.small,
      padding: theme.spacing(5, 3),
      boxShadow: "0px 2px 2px 0px #0000001F",
      spacing: 2,
    },
  })
);
